import { Controller } from "stimulus";

// Dismissible flash / toast notices, site-wide.
//
// Rails flash is one-shot per request, but Turbolinks caches the rendered page
// and restores it on back/forward and as the "preview" of the next visit — so a
// notice the reader already closed can flash back onto the screen from that
// cache. connect() runs on every Turbolinks render (including cache restores),
// so it is where we both wire the close button and re-hide anything already
// dismissed this session.
//
// The dismissed set lives in sessionStorage keyed by a signature of the
// message, so it clears when the tab closes (a genuinely new sign-in on the
// same tab still gets to say "You are already signed in.") and never grows
// without bound.
export default class extends Controller {
  static values = { signature: String };

  static STORAGE_KEY = "avDismissedFlash";
  static MAX_REMEMBERED = 50;

  connect() {
    if (this.signature && this.constructor.remembered().includes(this.signature)) {
      // Already closed this session — a Turbolinks cache restore is trying to
      // bring it back. Remove it before paint, no animation.
      this.element.remove();
      return;
    }
    this.element.classList.add("av-flash--ready");
  }

  dismiss() {
    if (this.signature) this.constructor.remember(this.signature);
    this.remove();
  }

  remove() {
    const el = this.element;
    el.classList.add("av-flash--closing");
    // Let the fade run, then drop it from the DOM. Guard against the element
    // having already gone (a second click, a Turbolinks visit mid-fade).
    const done = () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
    let settled = false;
    const once = () => {
      if (settled) return;
      settled = true;
      done();
    };
    el.addEventListener("transitionend", once, { once: true });
    // Fallback for reduced-motion / no transition — never leave it hanging.
    window.setTimeout(once, 400);
  }

  get signature() {
    return this.hasSignatureValue ? this.signatureValue : "";
  }

  static remembered() {
    try {
      const raw = window.sessionStorage.getItem(this.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  static remember(signature) {
    try {
      const list = this.remembered().filter((s) => s !== signature);
      list.push(signature);
      while (list.length > this.MAX_REMEMBERED) list.shift();
      window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      // sessionStorage can be unavailable (private mode, blocked cookies).
      // Dismissal still works for the life of the page; only the cross-cache
      // memory is lost, which is a graceful degradation, not a break.
    }
  }
}
